import React from 'react';
import { toast } from 'react-toastify';
import sign from 'jwt-encode';

import { LoadingTemplate } from '../../../../templates/LoadingTemplate';
import { ModalNoEligibility } from '../../components/ModalNoEligibility';
import useOnCloseSchedule from '../../../../hooks/useOnCloseSchedule';
import { Page, useCareplusRouter } from '../../router/context';
import {
  fetchHealthPlanBySubGroup,
  fetchPublicSpecialties,
  fetchSpecialistByIdV2
} from '../../../../modules/specialists';
import { useSpecialties } from '../../store/Specialties';
import { Specialty } from '../../../../models/Specialist';
import { envConfig } from '../../../../utils/enviroment';
import { ModalAlreadyScheduled } from '../../components/ModalAlreadyScheduled';
import { AlreadyScheduledError } from '../../../../errors/AlreadyScheduledError';
import { useSchedule } from '../../store/Schedule';
import { usePatientContext } from '../../../../contexts/PatientContext';
import { splitNameAndLastname } from '../../../../utils/formatters';
import { useCareTeam } from '../../store/CareTeam';
import { AppointmentBookApi, BusnessRule, specialistsApiV2 } from '../../../../services/api';
import { findOnePatientQuery } from '../../../../graphql/binding';
import { useAttendance } from '../../store/Attendance';
import { setCommonHeader } from '../../../../services/headers';
import { Appointments, fetchAppointments } from '../../../../modules/appointmentBook';
import { useAppointments } from '../../store/Appointment';
import { useHealthPlan } from '../../store/HealthPlan';
import { ModalSystemUnavailable } from '../../components/ModalSystemUnavailable';
import { useModuleContext } from '../../../../contexts/ModuleContext';
import { getEnabledMedicalCareList, getSpecialtyMedicalCareList } from '../../utils';
import { ModalImpediment } from '../../components/ModalImpediment';

type IndexPageProps = {
  sdkAuthorization?: string;
  isFirstAppointment?: boolean;
  scheduleData?: Appointments;
};

export const IndexPage = (state: IndexPageProps) => {
  const [isModalNoEligibilityOpen, setIsModalNoEligibilityOpen] = React.useState(false);
  const [isModalAlreadyScheduledOpen, setIsModalAlreadyScheduledOpen] = React.useState(false);
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalImpedimentVisible, setIsModalImpedimentVisible] = React.useState(false);

  const { onCloseSchedule } = useOnCloseSchedule({});
  const { patientSelected } = usePatientContext();
  const { sdkToken } = useModuleContext();

  const { navigate } = useCareplusRouter();

  const { setSpecialties, setSpecialtySelected } = useSpecialties();
  const { setIsFirstAppointment } = useAttendance();
  const { setCareTeam } = useCareTeam();
  const { setChannel, setPatient, scheduleBuild, setMedicalCare } = useSchedule();
  const { setAppointments, setSelectedAppointment } = useAppointments();
  const { setHealthPlan } = useHealthPlan();

  const checkBiding = async (document: string) => {
    const result = await BusnessRule.post('/care-team/graphql', findOnePatientQuery({ document }));

    if (result.data?.data?.findOnePatient?.edges?.length) {
      const [{ node }] = result.data.data.findOnePatient.edges;

      if (node?.healthProfessionals) setCareTeam(node.healthProfessionals);
    }
  };

  const getSpecialties = async () => {
    const result = await fetchPublicSpecialties();
    if (result.error) toast.error('Não foi possível buscar as especialidades.');
    if (!result.error) {
      const sortResult = result.data?.sort((item1, item2) => item1.name.localeCompare(item2.name));
      setSpecialties(sortResult);
      return sortResult;
    }
  };

  const checkAlreadyHasFirstAppointment = (appointments: Appointments[]) => {
    if (state.isFirstAppointment) {
      const patientAppointments = appointments.filter(ap =>
        ap.resource.specialties.some(sp => sp.name.toUpperCase() === 'ENDOCRINOLOGIA')
      );

      if (
        patientAppointments.some(
          sh => sh.status === 'SCHEDULED' || sh.status === 'RESCHEDULED' || sh.status === 'CONFIRMED'
        )
      ) {
        throw new AlreadyScheduledError('Consulta com endocrinologista já agendada.');
      }
    }
  };

  const getAppointments = async () => {
    const result = await fetchAppointments({
      patientDocument: patientSelected?.document || '',
      startDate: new Date().toISOString().substring(0, 10)
    });

    if (result.length) {
      setAppointments(result);
      checkAlreadyHasFirstAppointment(result);
    }
  };

  const handleFirstAppointment = async (specialties: Specialty[]) => {
    const specialty = specialties.find(item => item.id === envConfig.REACT_APP_CARE_PLUS_FIRST_APPOINTMENT_SPECIALTY);
    if (!specialty) {
      toast.error('Não foi possível seguir com o agendamento');
      onCloseSchedule();
    }

    if (specialty) {
      const specialtyMedicalCares = await getSpecialtyMedicalCareList(specialty.id);
      const enabledMedicalCareList = getEnabledMedicalCareList(specialtyMedicalCares);

      if (!specialtyMedicalCares.length || !enabledMedicalCareList) {
        setIsModalImpedimentVisible(true);
      } else {
        setSpecialtySelected(specialty);
        setMedicalCare({ catalogId: enabledMedicalCareList.id, name: enabledMedicalCareList.name });
        scheduleBuild();
        navigate(Page.UNITS, { IS_FIRST_APPOINTMENT: state?.isFirstAppointment });
      }
    }
  };

  const handleReturnAppointment = (specialties: Specialty[]) => {
    navigate(Page.SPECIALTY_LIST, { IS_FIRST_APPOINTMENT: state?.isFirstAppointment });
  };

  const handleReschedule = async (appointment: Appointments) => {
    const specialist = await getSpecialistById(appointment.resource.externalId);

    if (specialist) {
      appointment.resource.catalogId = specialist.id;

      setSelectedAppointment(appointment);
      navigate(Page.PRODUCT_SLOTS, { IS_FIRST_APPOINTMENT: false });
    }
  };

  const handleError = (error: Error) => {
    if (error instanceof AlreadyScheduledError) setIsModalAlreadyScheduledOpen(true);
    else setIsModalSystemUnavailableVisible(r => !r);
  };

  const checkScheduleAvailability = async () => {
    try {
      if (state?.scheduleData?.id) return handleReschedule(state?.scheduleData);

      await checkBiding(patientSelected?.document || '');

      await getHeathPlan();

      await getAppointments();

      const specialtiesResult = await getSpecialties();

      if (!specialtiesResult) onCloseSchedule();

      if (specialtiesResult?.length && state?.isFirstAppointment) handleFirstAppointment(specialtiesResult);
      if (specialtiesResult?.length && !state?.isFirstAppointment) handleReturnAppointment(specialtiesResult);
    } catch (error: any) {
      handleError(error);
    }
  };

  const getSpecialistById = async (specialistId: string) => {
    const result = await fetchSpecialistByIdV2(specialistId);

    if (!result.error) return result.data;
    if (result.error) onCloseSchedule();
  };

  const getHeathPlan = async () => {
    const result = await fetchHealthPlanBySubGroup({ value: patientSelected?.eligibilitySubGroup || '' });
    if (result) setHealthPlan(result);
  };

  React.useEffect(() => {
    checkScheduleAvailability();

    setIsFirstAppointment(!!state?.isFirstAppointment);

    setCommonHeader(AppointmentBookApi, 'Authorization', `Bearer ${state.sdkAuthorization}`);
    setCommonHeader(specialistsApiV2, 'Authorization', `Bearer ${state.sdkAuthorization}`);
  }, []);

  React.useEffect(() => {
    if (patientSelected) {
      const { firstName, lastName } = splitNameAndLastname(patientSelected.name);

      setPatient({
        birthDate: patientSelected?.birthDate,
        document: patientSelected?.document,
        documentType: patientSelected?.documentType,
        email: patientSelected?.email,
        firstName,
        lastName,
        phone: patientSelected.phone,
        ddi: 'BR',
        sex: patientSelected.gender === 'Masculino' ? 'M' : 'F',

        externalId: String(patientSelected.idLife) || '',
        lifeId: String(patientSelected.idLife) || '',
        healthCard: patientSelected.eligibilityCardNumber || '',
        subGroup: patientSelected.eligibilitySubGroup || ''
      });
      setChannel('CARE_PLUS');

      scheduleBuild();
    }
  }, [patientSelected]);

  return (
    <>
      <LoadingTemplate />
      <ModalNoEligibility visible={isModalNoEligibilityOpen} onClose={onCloseSchedule} />
      <ModalAlreadyScheduled
        visible={isModalAlreadyScheduledOpen}
        onClose={onCloseSchedule}
        handleOpeningScheduleModule={() => {
          const objToken = {
            patient: patientSelected,
            authorization: sdkToken
          };

          const jwtToken = sign(objToken, '');

          window.location.href = `${process.env.REACT_APP_APPOINTMENT_MODULE_URL}?token=${jwtToken}`;
        }}
      />
      <ModalSystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={onCloseSchedule}
        callToSupport={() => (location.href = 'https://wa.me/11964079032?whatsapp=11964079032')}
        goToHome={onCloseSchedule}
      />
      <ModalImpediment
        visible={isModalImpedimentVisible}
        onClose={() => setIsModalImpedimentVisible(false)}
        callToSupport={() => (location.href = 'https://wa.me/11964079032?whatsapp=11964079032')}
        goToHome={onCloseSchedule}
      />
    </>
  );
};
